import * as preact from "preact";
import * as _ from "underscore";

import * as accountModal from "accountModal";
import * as featureFlags from "featureFlags";
import * as genericModals from "genericModals";
import models from "models/models";
import * as notificationUtils from "notificationUtils";
import * as practiceUtils from "practiceUtils";
import readlangClient from "readlangClient";
import * as readlangLocalStorage from "readlangLocalStorage";
import * as languageMap from "shared/languageMap";
import * as textToSpeech from "textToSpeech";
import EditDictionaryView from "views/EditDictionaryView";
import NotificationSettingsView from "views/NotificationSettingsView";

type Props = {
  appRouter: any;
};

type State = {
  hasNewPracticeModes: boolean;
};

class SettingsPageView extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    models.user.on("change", this.onUserChange);

    this.determineEligibilityForNewPractice();

    // Re-fetch user to ensure we have up-to-date data
    models.user.fetch();
  }

  async determineEligibilityForNewPractice() {
    this.setState({
      hasNewPracticeModes: await practiceUtils.hasNewPracticeModes(models.user),
    });
  }

  componentWillUnmount() {
    models.user.off("change", this.onUserChange);
  }

  onUserChange = () => {
    this.forceUpdate();
  };

  render() {
    const languageString =
      languageMap.map[models.user.get("currentLearningLanguage")].english;
    const firstLanguageString =
      languageMap.map[models.user.get("firstLanguage")].english;
    const speakingMode = models.user.get("speakingMode");
    const reminderEmail = notificationUtils.has(
      "dailyReminder",
      models.user.get("currentLearningLanguage")
    );
    const speakingLanguages = _.map(
      textToSpeech.getSupportedLanguages(),
      (code) => languageMap.map[code].english
    ).sort();
    const speakingModeSupported = models.user.hasSpeakingModeAccess(
      models.user.get("currentLearningLanguage")
    );
    const showDeleteAccount =
      !models.user.has("renewalMonths") ||
      models.user.get("renewalMonths") === 0;
    // Remove the following after running for a bit
    const newNotificationsView = true;

    const relevantFeatureFlags = featureFlags.userFlags.filter(
      (flag) =>
        flag.experimental &&
        (flag.availableTo === "free" ||
          (flag.availableTo === "premium" && models.user.hasPremiumAccess()) ||
          (flag.availableTo === "god" && models.user.isGod()))
    );

    return (
      <div>
        <h2 class="spacing">
          <i class="icon-cog"></i> General Settings
        </h2>
        <table class="settingsTable">
          <tbody>
            <tr>
              <td>
                <input
                  type="checkbox"
                  checked={models.user.hasFeature("contextTranslations")}
                  id="contextAwareTranslationsCheckbox"
                  onClick={(event) => {
                    if (
                      !models.user.hasFeature("contextTranslations") &&
                      !models.user.hasPremiumAccess()
                    ) {
                      accountModal.show({
                        model: models.user,
                        title: "contextTranslations",
                        messageHtml:
                          "<h2>Upgrade to enable context aware translations...</h2>",
                        showTitle: false,
                      });
                      event.preventDefault();
                      return;
                    }

                    models.user.setFeature(
                      "contextTranslations",
                      !models.user.hasFeature("contextTranslations")
                    );
                  }}
                />
              </td>
              <td>
                <label htmlFor="contextAwareTranslationsCheckbox">
                  Context aware translations{" "}
                  <small>
                    - takes into account the surrounding sentence for more
                    accurate translations
                  </small>
                </label>
              </td>
            </tr>
            <tr>
              <td>
                <input
                  type="checkbox"
                  checked={models.user.hasFeature("betterAIModel")}
                  id="betterAIModelCheckbox"
                  onClick={(event) => {
                    if (
                      !models.user.hasFeature("betterAIModel") &&
                      !models.user.hasPremiumPlusAccess()
                    ) {
                      accountModal.show({
                        model: models.user,
                        title: "betterAIModel",
                        messageHtml:
                          "<h2>Upgrade to Premium Plus for better AI model</h2>",
                        showTitle: false,
                        plus: true,
                      });
                      event.preventDefault();
                      return;
                    }

                    models.user.setFeature(
                      "betterAIModel",
                      !models.user.hasFeature("betterAIModel")
                    );
                  }}
                />
              </td>
              <td>
                <label htmlFor="betterAIModelCheckbox">
                  Better AI Model{" "}
                  <small>
                    Uses state-of-the-art GPT-4o model for AI powered features
                    (including context aware translations and explanations)
                    instead of GPT-4o mini
                  </small>
                </label>
              </td>
            </tr>
            <tr>
              <td>
                <input
                  type="checkbox"
                  checked={speakingMode}
                  id="speakingModeCheckbox"
                  onClick={(event) => {
                    if (!speakingMode && !models.user.hasPremiumAccess()) {
                      accountModal.show({
                        model: models.user,
                        title: "speakingMode",
                        messageHtml:
                          "<h2>Upgrade to enable speaking mode...</h2>",
                        showTitle: false,
                      });
                      event.preventDefault();
                      return;
                    }

                    models.user.save(
                      { speakingMode: !speakingMode },
                      { patch: true }
                    );
                  }}
                />
              </td>
              <td>
                <label htmlFor="speakingModeCheckbox">
                  Speaking mode{" "}
                  <small>
                    - speaks every time you translate a word or practice a
                    flashcard. Works for{" "}
                    <span title={speakingLanguages.join(", ")}>
                      {speakingLanguages.length} languages
                    </span>
                    ,
                    {speakingModeSupported
                      ? ` including ${languageString}`
                      : ` but unfortunately not ${languageString}`}
                  </small>
                </label>
              </td>
            </tr>
            <tr>
              <td>
                <input
                  type="checkbox"
                  checked={models.user.get("soundEffectsEnabled")}
                  id="soundEffectsCheckbox"
                  onClick={() => {
                    models.user.save(
                      {
                        soundEffectsEnabled: !models.user.get(
                          "soundEffectsEnabled"
                        ),
                      },
                      { patch: true }
                    );
                  }}
                />
              </td>
              <td>
                <label htmlFor="soundEffectsCheckbox">
                  Sound effects{" "}
                  <small>
                    - plays a sound when you get a word right or wrong
                  </small>
                </label>
              </td>
            </tr>
          </tbody>
        </table>
        <h2 class="spacing">
          <i class="icon-cog"></i> Flashcard Mode
        </h2>
        <p>
          All flashcards start in passive mode ({languageString} -&gt;{" "}
          {firstLanguageString}) the first time you see them. This setting
          affects the subsequent appearances.{" "}
          {this.state.hasNewPracticeModes ? (
            <small>
              (Applies both to "Classic Flashcards" and "Mastery" practice. In
              "Mastery" mode, the "Passive Recall Only" setting will make new
              words and phrases default to passive.)
            </small>
          ) : (
            ""
          )}
        </p>
        <table class="settingsTable">
          <tbody>
            <tr>
              <td>
                <input
                  type="radio"
                  name="flashcardMode"
                  value="passive"
                  id="passiveModeRadio"
                  checked={models.user.getFlashcardMode() === "passive"}
                  onClick={() => models.user.setFlashcardMode("passive")}
                />
              </td>
              <td>
                <label htmlFor="passiveModeRadio">
                  Passive Recall Only{" "}
                  <small>(easiest, only practice reading comprehension)</small>
                </label>
              </td>
            </tr>
            <tr>
              <td>
                <input
                  type="radio"
                  name="flashcardMode"
                  value="mixed"
                  id="mixedModeRadio"
                  checked={models.user.getFlashcardMode() === "mixed"}
                  onClick={() => models.user.setFlashcardMode("mixed")}
                />
              </td>
              <td>
                <label htmlFor="mixedModeRadio">
                  Mix of Active and Passive Recall <small>(recommended)</small>
                </label>
              </td>
            </tr>
            <tr>
              <td>
                <input
                  type="radio"
                  name="flashcardMode"
                  value="active"
                  id="activeModeRadio"
                  checked={models.user.getFlashcardMode() === "active"}
                  onClick={() => models.user.setFlashcardMode("active")}
                />
              </td>
              <td>
                <label htmlFor="activeModeRadio">Mainly Active Recall</label>
              </td>
            </tr>
          </tbody>
        </table>
        {newNotificationsView ? (
          <>
            <h2 class="spacing">
              <i class="icon-cog"></i> Daily Reminders
            </h2>
            <NotificationSettingsView user={models.user} />
          </>
        ) : (
          <div>
            <h2 class="spacing">
              <i class="icon-cog"></i> {languageString} Email Reminders
            </h2>

            <label>
              <input
                type="checkbox"
                class="reminderEmail"
                checked={reminderEmail}
              />{" "}
              Send Daily {languageString} Reminder Emails
              <small>
                {" "}
                - if you haven't read 500 words or practiced 10 flashcards yet
              </small>
            </label>
          </div>
        )}
        <h2 class="spacing">
          <i class="icon-cog"></i> Custom {languageString} Dictionary
        </h2>
        <p>
          This allows you to use any online {languageString} dictionary in
          Readlang's sidebar.
        </p>
        <EditDictionaryView
          key={models.user.get("currentLearningLanguage")}
          user={models.user}
        />
        {relevantFeatureFlags.length > 0 ? (
          <>
            <h2 class="spacing" id="experimentalFeatures">
              <i class="icon-cog"></i> Experimental Features
            </h2>
            <p>
              Enable or disable experimental features. This allows early access
              to features which may or may not stick around for the long term.
            </p>
            <table class="settingsTable">
              <tbody>
                {relevantFeatureFlags.map((flag) => (
                  <tr>
                    <td>
                      <input
                        type="checkbox"
                        class={flag.key}
                        checked={models.user.hasFeature(flag.key)}
                        id={`featureFlag-${flag.key}`}
                        onClick={() => {
                          models.user.setFeature(
                            flag.key,
                            !models.user.hasFeature(flag.key)
                          );
                        }}
                      />
                    </td>
                    <td>
                      <label for={`featureFlag-${flag.key}`}>
                        <strong>{flag.key}</strong>{" "}
                        {flag.availableTo === "premium"
                          ? "(premium only)"
                          : null}{" "}
                        {flag.description}
                      </label>{" "}
                      {flag.forumLink ? (
                        <>
                          <small>
                            <a href={flag.forumLink} target="_blank">
                              Discuss on forum
                            </a>
                            .
                          </small>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}
        {showDeleteAccount && (
          <>
            <h2 class="spacing">
              <i class="icon-cog"></i> Your Readlang Account
            </h2>

            <p>
              <a
                href="/api/exportAllData.txt"
                class="exportAccountDataLink"
                target="_blank"
                data-bypass="true"
              >
                Export all personal data stored in my Readlang account
              </a>
            </p>
            <p>
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  this.deleteAccount();
                }}
              >
                <i class="icon-warning-sign"></i> Delete my Readlang account
                along with all personal data
              </a>
            </p>
          </>
        )}
        <br />
        &nbsp;
      </div>
    );
  }

  deleteAccount = () => {
    genericModals.confirm({
      title: "Permanently Delete Your Readlang Account",
      warning: true,
      message:
        "Are you sure you want to **permanently delete** your account, including **all your " +
        "texts and words**?",
      callback: (response) => {
        if (response === "ok") {
          genericModals.confirm({
            title: "Seriously, there's no way back from this!",
            warning: true,
            message:
              "Are you absolutely sure you want to **permanently delete** your account, " +
              "including **all your texts and words**?",
            callback: (innerResponse) => {
              if (innerResponse === "ok") {
                readlangClient.request(
                  "DELETE",
                  "/api/user/" + models.user.id,
                  {
                    confirmation: "yes i really want to delete this",
                  },
                  () => {
                    genericModals.alert({
                      message: "Account deleted! Logging out now...",
                    });
                    localStorage.clear();
                    readlangLocalStorage.clear(() => {
                      setTimeout(() => {
                        window.location.href = "/landing-page";
                      }, 2000);
                    });
                  },
                  () => {
                    genericModals.alert({
                      message: "Delete account failed",
                    });
                  }
                );
              }
            },
          });
        }
      },
    });
  };
}

export default SettingsPageView;
